import { objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2, extends as _extends, defineProperty as _defineProperty } from '../../../../_virtual/_rollupPluginBabelHelpers.esm.js';
import { useObjectRef, filterDOMProps } from '@react-aria/utils';
import React, { useContext, useRef, useEffect } from 'react';
import { useGridListItem, useHover, useFocusRing, useGridListSelectionCheckbox, useVisuallyHidden, mergeProps } from 'react-aria';
import { ListStateContext, DragAndDropContext, DropIndicator, Provider, CheckboxContext, ButtonContext, TextContext } from 'react-aria-components';
import { useRenderProps, DEFAULT_SLOT } from '../../../utils/index.esm.js';

var _excluded = ["rowProps", "gridCellProps", "descriptionProps"];
function GridListRow(_ref) {
  var _dropIndicator, _dropIndicator2, _draggableItem, _dropIndicator3, _draggableItem2;
  var item = _ref.item;
  var state = useContext(ListStateContext);
  var _useContext = useContext(DragAndDropContext),
    dragAndDropHooks = _useContext.dragAndDropHooks,
    dragState = _useContext.dragState,
    dropState = _useContext.dropState;
  var ref = useObjectRef(item.props.ref);
  var _useGridListItem = useGridListItem({
      node: item,
      shouldSelectOnPressUp: !!dragState
    }, state, ref),
    rowProps = _useGridListItem.rowProps,
    gridCellProps = _useGridListItem.gridCellProps,
    descriptionProps = _useGridListItem.descriptionProps,
    states = _objectWithoutProperties(_useGridListItem, _excluded);
  var _useHover = useHover({
      isDisabled: !states.allowsSelection && !states.hasAction,
      onHoverStart: item.props.onHoverStart,
      onHoverChange: item.props.onHoverChange,
      onHoverEnd: item.props.onHoverEnd
    }),
    hoverProps = _useHover.hoverProps,
    isHovered = _useHover.isHovered;
  var _useFocusRing = useFocusRing(),
    isFocusVisible = _useFocusRing.isFocusVisible,
    focusProps = _useFocusRing.focusProps;
  var _useGridListSelection = useGridListSelectionCheckbox({
      key: item.key
    }, state),
    checkboxProps = _useGridListSelection.checkboxProps;
  var draggableItem = null;
  if (dragState && dragAndDropHooks) {
    draggableItem = dragAndDropHooks.useDraggableItem({
      key: item.key,
      hasDragButton: true
    }, dragState);
  }
  var dropIndicator = null;
  var dropIndicatorRef = useRef(null);
  var _useVisuallyHidden = useVisuallyHidden(),
    visuallyHiddenProps = _useVisuallyHidden.visuallyHiddenProps;
  if (dropState && dragAndDropHooks) {
    dropIndicator = dragAndDropHooks.useDropIndicator({
      target: {
        type: 'item',
        key: item.key,
        dropPosition: 'on'
      }
    }, dropState, dropIndicatorRef);
  }
  var props = item.props;
  var isDragging = dragState && dragState.isDragging(item.key);
  var renderProps = useRenderProps(_objectSpread2(_objectSpread2({}, props), {}, {
    id: undefined,
    children: item.rendered,
    defaultClassName: 'react-aria-GridListItem',
    values: _objectSpread2(_objectSpread2({}, states), {}, {
      isHovered: isHovered,
      isFocusVisible: isFocusVisible,
      selectionMode: state.selectionManager.selectionMode,
      selectionBehavior: state.selectionManager.selectionBehavior,
      allowsDragging: !!dragState,
      isDragging: isDragging,
      isDropTarget: (_dropIndicator = dropIndicator) === null || _dropIndicator === void 0 ? void 0 : _dropIndicator.isDropTarget
    })
  }));
  var renderDropIndicator = (dragAndDropHooks === null || dragAndDropHooks === void 0 ? void 0 : dragAndDropHooks.renderDropIndicator) || function (target) {
    return /*#__PURE__*/React.createElement(DropIndicator, {
      target: target
    });
  };
  var dragButtonRef = useRef(null);
  useEffect(function () {
    if (dragState && !dragButtonRef.current) {
      // eslint-disable-next-line no-console
      console.warn('Draggable items in a GridList must contain a <Button slot="drag"> element so that keyboard and screen reader users can drag them.');
    }
    // eslint-disable-next-line
  }, []);
  useEffect(function () {
    if (!item.textValue) {
      // eslint-disable-next-line no-console
      console.warn('A `textValue` prop is required for <GridListItem> elements with non-plain text children in order to support accessibility features such as type to select.');
    }
  }, [item.textValue]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, (dragAndDropHooks === null || dragAndDropHooks === void 0 ? void 0 : dragAndDropHooks.useDropIndicator) && renderDropIndicator({
    type: 'item',
    key: item.key,
    dropPosition: 'before'
  }), dropIndicator && !dropIndicator.isHidden && /*#__PURE__*/React.createElement("div", {
    role: "row",
    className: "absolute"
  }, /*#__PURE__*/React.createElement("div", {
    role: "gridcell"
  }, /*#__PURE__*/React.createElement("div", _extends({
    role: "button"
  }, visuallyHiddenProps, (_dropIndicator2 = dropIndicator) === null || _dropIndicator2 === void 0 ? void 0 : _dropIndicator2.dropIndicatorProps, {
    ref: dropIndicatorRef
  })))), /*#__PURE__*/React.createElement("div", _extends({}, mergeProps(filterDOMProps(props), rowProps, focusProps, hoverProps, (_draggableItem = draggableItem) === null || _draggableItem === void 0 ? void 0 : _draggableItem.dragProps), renderProps, {
    ref: ref,
    "data-selected": states.isSelected || undefined,
    "data-disabled": states.isDisabled || undefined,
    "data-hovered": isHovered || undefined,
    "data-focused": states.isFocused || undefined,
    "data-focus-visible": isFocusVisible || undefined,
    "data-pressed": states.isPressed || undefined,
    "data-allows-dragging": !!dragState || undefined,
    "data-dragging": isDragging || undefined,
    "data-drop-target": ((_dropIndicator3 = dropIndicator) === null || _dropIndicator3 === void 0 ? void 0 : _dropIndicator3.isDropTarget) || undefined,
    "data-selection-mode": state.selectionManager.selectionMode === 'none' ? undefined : state.selectionManager.selectionMode
  }), /*#__PURE__*/React.createElement("div", _extends({}, gridCellProps, {
    className: "contents"
  }), /*#__PURE__*/React.createElement(Provider
  // eslint-disable-next-line react-perf/jsx-no-new-array-as-prop
  , {
    values: [[CheckboxContext, {
      slots: {
        selection: checkboxProps
      }
    }], [ButtonContext, {
      slots: _defineProperty(_defineProperty({}, DEFAULT_SLOT, {}), "drag", _objectSpread2(_objectSpread2({}, (_draggableItem2 = draggableItem) === null || _draggableItem2 === void 0 ? void 0 : _draggableItem2.dragButtonProps), {}, {
        ref: dragButtonRef,
        style: {
          pointerEvents: 'none'
        }
      }))
    }], [TextContext, {
      slots: {
        description: descriptionProps
      }
    }]]
  }, renderProps.children))), (dragAndDropHooks === null || dragAndDropHooks === void 0 ? void 0 : dragAndDropHooks.useDropIndicator) && state.collection.getKeyAfter(item.key) == null && renderDropIndicator({
    type: 'item',
    key: item.key,
    dropPosition: 'after'
  }));
}

export { GridListRow as default };
//# sourceMappingURL=grid-list-row.esm.js.map
